import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaDownload, FaFilePdf, FaSpinner } from 'react-icons/fa';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';

// ── Lecteur PDF interne (fetch en blob + rendu react-pdf, scroll continu) ──
const PdfReader = ({ url }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pages, setPages] = useState(0);
    const [width, setWidth] = useState(320);
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setLoading(true);
        setError(false);
        setBlobUrl(null);
        setPages(0);

        const load = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(objectUrl);
            } catch (e) {
                console.error('Erreur de lecture du document PDF :', e);
                if (!cancelled) setError(true);
                setLoading(false);
            }
        };
        if (url) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.clientWidth - 16);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [blobUrl]);

    if (error) {
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <h6>Aperçu indisponible</h6>
                <p style={{ color: '#6c757d', marginTop: 8 }}>
                    Ce document PDF n'a pas pu être chargé pour lecture.
                </p>
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f1f3f5', padding: '0 8px 16px' }}>
            {(loading || !blobUrl) && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 0' }}>
                    <FaSpinner className="fa-spin" size={32} color="#28a745" />
                    <p style={{ marginTop: 16, color: '#6c757d' }}>Chargement du document…</p>
                </div>
            )}
            {blobUrl && (
                <Document
                    file={blobUrl}
                    onLoadSuccess={({ numPages: n }) => { setPages(n); setLoading(false); }}
                    onLoadError={(e) => { console.error('Erreur pdf.js :', e); setError(true); setLoading(false); }}
                    loading={null}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        {Array.from({ length: pages }, (_, i) => (
                            <div key={i} style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.15)', background: '#fff' }}>
                                <Page pageNumber={i + 1} width={width > 0 ? width : 320} />
                            </div>
                        ))}
                    </div>
                </Document>
            )}
        </div>
    );
};

// ── Modal principale ─────────────────────────────────────────────────────
const PDFViewerModal = ({
                            show,
                            onHide,
                            pdfUrl,
                            title = "Document PDF",
                            contractId = null,
                        }) => {
    if (!show) return null;

    const getFileName = () => contractId ? `Document ${contractId}` : title;

    const handleDownload = () => {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `document${contractId ? `_${contractId}` : ''}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div
            className="pdf-preview-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.8)',
                zIndex: 1300,
                overflowY: 'auto',
            }}
            onClick={onHide}
        >
            <div
                className="pdf-preview-sheet"
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    borderRadius: '24px 24px 0 0',
                    height: '95vh',
                    maxHeight: '95vh',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'pdfPreviewSlideUp 0.35s ease',
                    width: '100%',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header sticky */}
                <div style={{
                    flex: '0 0 auto',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e2e8f0',
                    borderTopLeftRadius: '24px',
                    borderTopRightRadius: '24px',
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <img src={PDF_ICON_URL} alt="PDF" style={{ width: 26, height: 26, flexShrink: 0 }} />
                        <h6 style={{ margin: 0, fontWeight: 700, color: '#1a202c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getFileName()}
                        </h6>
                    </div>
                    <button
                        onClick={onHide}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6c757d',
                            padding: '0.5rem',
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Corps scrollable — seul le lecteur PDF scrolle */}
                <div style={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    {pdfUrl ? (
                        <PdfReader url={pdfUrl} />
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <FaFilePdf size={64} className="text-muted mb-3 opacity-50" />
                            <p className="text-muted">Aucun PDF disponible</p>
                        </div>
                    )}
                </div>

                {/* Footer sticky */}
                <div style={{
                    flex: '0 0 auto',
                    backgroundColor: 'white',
                    borderTop: '1px solid #e2e8f0',
                    padding: '14px 20px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    flexWrap: 'wrap',
                }}>
                    <button
                        onClick={onHide}
                        style={{
                            padding: '8px 24px',
                            borderRadius: '50px',
                            border: '1px solid #6c757d',
                            backgroundColor: 'transparent',
                            color: '#6c757d',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#6c757d';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#6c757d';
                        }}
                    >
                        <FaTimes className="me-1" /> Fermer
                    </button>
                    {pdfUrl && (
                        <button
                            onClick={handleDownload}
                            style={{
                                padding: '8px 24px',
                                borderRadius: '50px',
                                border: 'none',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                        >
                            <FaDownload /> Télécharger
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes pdfPreviewSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                .pdf-preview-overlay {
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default PDFViewerModal;